/**
 * Chart Watermark Utility
 * Adds Batemo logo watermark to ECharts plots
 * Watermark appears in exported images (save as image)
 */

/**
 * Get watermark graphic configuration for ECharts
 * Positions logo inside grid area using absolute positioning from container edges
 * @param {Object} options - Watermark options
 * @param {string} options.position - Position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
 * @param {number} options.opacity - Opacity (0-1), default 0.12
 * @param {number} options.width - Logo width in pixels, default 140
 * @param {number} options.height - Logo height in pixels, default 40
 * @param {Object} options.grid - Optional grid config {left, right, top, bottom} for precise positioning
 * @returns {Object} ECharts graphic configuration
 */
export function getWatermarkGraphic(options = {}) {
  const {
    position = 'top-right',
    opacity = 0.12,
    width = 236,
    height = 60,
    grid = null
  } = options

  // Default grid values (typical for our charts)
  const gridLeft = grid?.left || 80
  const gridRight = grid?.right || 40
  const gridTop = grid?.top || 60
  const gridBottom = grid?.bottom || 80

  // Padding from grid edge
  const padding = 10

  // Position configurations using left/right/top/bottom from container edges
  // These values account for grid offset to position inside the grid area
  const positions = {
    'top-right': { 
      right: gridRight + padding,
      top: gridTop + padding
    },
    'top-left': { 
      left: gridLeft + padding,
      top: gridTop + padding
    },
    'bottom-right': { 
      right: gridRight + padding,
      bottom: gridBottom + padding
    },
    'bottom-left': { 
      left: gridLeft + padding,
      bottom: gridBottom + padding
    }
  }

  const config = positions[position] || positions['top-right']

  return {
    type: 'image',
    ...config,
    style: {
      image: '/logo/batemo_logo_white.svg',
      width: width,
      height: height,
      opacity: opacity
    },
    silent: true,
    z: 100
  }
}

/**
 * Get watermark for 3D charts (uses absolute positioning as grid3D has different coord system)
 */
export function get3DWatermarkGraphic(options = {}) {
  const {
    opacity = 0.12,
    width = 236,
    height = 60
  } = options

  return {
    type: 'image',
    style: {
      image: '/logo/batemo_logo.svg',
      width: width,
      height: height,
      opacity: opacity
    },
    right: '3%',
    top: '5%',
    silent: true,
    z: 100
  }
}

/**
 * Add watermark text (optional, for additional branding)
 */
export function getWatermarkText(options = {}) {
  const {
    text = 'batemo.com',
    position = 'bottom-right',
    opacity = 0.2,
    fontSize = 12
  } = options

  const positions = {
    'bottom-right': { right: 20, bottom: 5 },
    'bottom-left': { left: 20, bottom: 5 },
    'top-right': { right: 20, top: 5 },
    'top-left': { left: 20, top: 5 }
  }

  const pos = positions[position] || positions['bottom-right']

  return {
    type: 'text',
    style: {
      text: text,
      font: '12px Roboto, sans-serif',
      fill: '#94a3b8',
      opacity: opacity
    },
    ...pos,
    silent: true,
    z: 1
  }
}
